/**
 * ManualSeverityPencil — the inline severity picker NTN staff uses to set
 * the applicant + household hero card. Colocated with the existing
 * RiskIntelligenceSummaryCard (no second card).
 *
 * `endpoint` — full path relative to /api  (e.g. `/staff-ops/case/{sid}/applicant-risk`
 *              or `/groups/{gid}/household-risk`)
 * `current`  — the currently-stored { severity, label, ... } (or null)
 * `onSaved`  — invoked with the new stored value
 */
import { useState } from "react";
import { apiClient } from "@/lib/api";
import { Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const OPTIONS = [
  { severity: "none",        tone: "green",  label: "No Risk Indicators Found" },
  { severity: "potential",   tone: "amber",  label: "Potential Risk Indicators Found" },
  { severity: "significant", tone: "orange", label: "Significant Risk Indicators Found" },
  { severity: "elevated",    tone: "red",    label: "Elevated Risk Indicators Found" },
];

const TONE = {
  green:  "bg-emerald-700",
  amber:  "bg-amber-500",
  orange: "bg-orange-600",
  red:    "bg-rose-700",
};

export default function ManualSeverityPencil({ endpoint, current, onSaved, testid = "manual-severity-pencil" }) {
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState(current?.severity || "");
  const [note, setNote] = useState(current?.note || "");
  const [busy, setBusy] = useState(false);

  const openDialog = () => {
    setSeverity(current?.severity || "");
    setNote(current?.note || "");
    setOpen(true);
  };

  const save = async () => {
    if (!severity) { toast.error("Choose a severity."); return; }
    setBusy(true);
    try {
      const { data } = await apiClient.post(endpoint, { severity, note: note || null });
      const key = Object.keys(data).find((k) => k.endsWith("_risk_summary"));
      toast.success("Manual risk saved.");
      onSaved?.(key ? data[key] : null);
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the risk.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 hover:text-[#0A192F] transition-colors rounded-sm px-2 py-1 hover:bg-slate-100"
        data-testid={testid}
        title="Set risk (NTN only)"
      >
        <Pencil className="h-3 w-3" /> Set risk
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`${testid}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Set risk</DialogTitle>
            <DialogDescription>
              Choose the severity Property Managers will see. Findings and
              indicators no longer drive this — you do.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {OPTIONS.map((o) => {
              const selected = severity === o.severity;
              return (
                <button
                  key={o.severity}
                  type="button"
                  onClick={() => setSeverity(o.severity)}
                  data-testid={`severity-opt-${o.severity}`}
                  className={`w-full text-left rounded-md border p-3 flex items-center gap-3 transition-all
                    ${selected ? `${TONE[o.tone]} text-white border-transparent ring-2 ring-offset-1 ring-slate-400` : "border-slate-200 hover:border-slate-400"}`}
                >
                  <span className={`h-2.5 w-2.5 rounded-full ${TONE[o.tone]}`} />
                  <div className={`font-serif text-base ${selected ? "text-white" : "text-slate-900"}`}>{o.label}</div>
                </button>
              );
            })}
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Internal note (optional)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                      rows={3} className="mt-1.5 rounded-sm" data-testid="severity-note" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="severity-cancel">Cancel</Button>
            <Button onClick={save} disabled={busy || !severity}
                    className="bg-[#0A192F] hover:bg-[#0A192F]/90" data-testid="severity-save">
              {busy ? "Saving…" : "Save risk"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
